import type { BoxProps } from '@hover/blueprint';
import { Body, Box } from '@hover/blueprint';
import React from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { useIsMaterialCalloutsEnabled } from 'src/modules/homeownerApp/hooks/split';
import { usePartnerMaterialCallout } from 'src/modules/homeownerApp/hooks/usePartner';
import type { VariationEdit } from 'src/modules/homeownerApp/hooks/useVariationEdits';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Button } from 'src/shared/components';
import { type AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { MaterialCard } from '../../Card';

export type Swatch = {
  uid: string;
  thumbnail?: string;
  color?: string;
  image?: string;
  manufacturer?: string;
  name?: string;
  region?: string;
  assets?: AiDisplayImageFragment[];
};

export interface ProjectSwatchProps extends BoxProps {
  edit: VariationEdit;
  projectId: string;
  title?: string;
}

const ProjectSwatch = ({
  edit: {
    assets,
    color,
    image,
    manufacturer,
    manufacturerUrl,
    name,
    region,
    thumbnail,
  },
  projectId,
  title,
}: ProjectSwatchProps) => {
  const history = useHistory();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { setCurrentMenu } = useHomeownerAppContext();

  const header = region ? (
    <Trans
      components={{
        Bold: <Body as="span" color="inherit" isSemiBold />,
      }}
      i18nKey="projects.swatch.cardHeader"
      t={t}
      values={{
        region,
        name,
      }}
    />
  ) : (
    name
  );

  const isMaterialCalloutsEnabled = useIsMaterialCalloutsEnabled();
  const callout = usePartnerMaterialCallout();

  return (
    <Box data-testid="project-swatch" flexDirection="column" gap="300">
      <Box mb={300} minHeight={600}>
        <MaterialCard
          backgroundColor={color}
          backgroundImage={image}
          count={assets?.length}
          coverImageAlt={t('projects.swatch.pictureInPictureAlt', {
            edit: color,
          })}
          coverImageUrl={thumbnail}
          cursor="pointer"
          header={header}
          onClick={() => {
            history.replace({
              pathname: location.pathname,
              state: {
                header: title,
                subtitle: region
                  ? t('projects.swatch.cardHeaderTextOnly', {
                      region,
                      name,
                    })
                  : name,
                assets,
                projectId,
              },
            });
            setCurrentMenu(HomeownerAppMenuNames.MATERIAL_SELECTION);
          }}
          subtitle={manufacturer}
        />
      </Box>
      {isMaterialCalloutsEnabled && manufacturerUrl && callout && (
        <Button
          as="a"
          href={manufacturerUrl}
          iconAfter={callout.icon}
          mb={{
            // On mobile the scroll container these cards live in clips the
            // focus halo on the button, so give it a little room in that case
            base: 100,
            desktop: 0,
          }}
          size="large"
          target="_blank"
        >
          {callout.label}
        </Button>
      )}
    </Box>
  );
};

export { ProjectSwatch };
